/**
 * Browser-side geometry check pipeline — runs automatically when BCA is
 * enabled on a 3D view. Measurements merge into the violations panel via
 * App.tsx (no separate "Run demo" step).
 */
import {
  type EvaluatedElement,
  evaluateRule,
  type RuleResult,
  type RuleSpec,
} from "@vera/rules";
import type { Violation } from "@vera/shared";
import { create } from "zustand";
import { getProject } from "../data/projects";
import { findWaterClosetsForGeometry } from "../viewer/geometry/findAccessibleWaterClosets";
import { measureClearAtDbId } from "../viewer/geometry/measureWashroomClearSize";
import { stageModelForRaycast } from "../viewer/geometry/rayCast";
import {
  BCA_513_LONG_MM,
  BCA_513_RULE_ID,
  BCA_513_SHORT_MM,
  type WashroomMeasureResult,
} from "../viewer/geometry/types";
import { visualizeViolation } from "../viewer/violationVisualizer";
import { useEngineStore } from "./engineStore";
import { useProjectStore } from "./projectStore";
import { useViewerStore } from "./viewerStore";

export interface GeometryCheckRow {
  measure: WashroomMeasureResult;
  element: EvaluatedElement;
  ruleResult: RuleResult | null;
  violation: Violation | null;
  pass: boolean | null;
  source: "washroom";
}

interface GeometryStore {
  status: "idle" | "running" | "done" | "error";
  error: string | null;
  rows: GeometryCheckRow[];
  /** Avoid duplicate ray-march when viewer re-renders. */
  lastRunKey: string | null;
  runGeometryChecks: (viewer: Autodesk.Viewing.GuiViewer3D) => Promise<void>;
  clear: () => void;
}

function findBca513Rule(rules: RuleSpec[]): RuleSpec | undefined {
  return rules.find(r => r.ruleId === BCA_513_RULE_ID);
}

function toEvaluatedElement(m: WashroomMeasureResult): EvaluatedElement {
  const facts: EvaluatedElement["facts"] = {};
  if (m.shortMm != null) {
    facts.clearFloorShortSide = { value: m.shortMm, unit: "millimetre" };
  }
  if (m.longMm != null) {
    facts.clearFloorLongSide = { value: m.longMm, unit: "millimetre" };
  }
  return {
    dbId: m.candidate.dbId,
    ontologyType: "vera:AccessibleWaterCloset",
    displayName: m.candidate.displayName,
    facts,
    factSources: {
      clearFloorShortSide: "geometry:ray-march",
      clearFloorLongSide: "geometry:ray-march",
    },
  };
}

function evaluatePass(m: WashroomMeasureResult): boolean | null {
  if (m.shortMm == null || m.longMm == null) return null;
  return m.shortMm >= BCA_513_SHORT_MM && m.longMm >= BCA_513_LONG_MM;
}

function ruleResultToGeometryViolation(
  rr: RuleResult,
  rule: RuleSpec,
  preferredSheet: string | undefined,
  runId: string
): Violation {
  const severity =
    rr.status === "confirmed_violation"
      ? rule.severity
      : rr.status === "indeterminate"
        ? "warning"
        : "info";
  return {
    id: `geo-${rule.ruleId}#${rr.dbId}`.replace(/[^A-Za-z0-9_#:.-]/g, "_"),
    code: `${rule.source.code} §${rule.source.section}`,
    summary: rr.summary,
    rationale: rr.rationale,
    elementDbId: rr.dbId,
    preferredSheetId: preferredSheet,
    severity,
    sourceQueryHandle: `${rule.source.jurisdictionId}:${rule.source.section}`,
    provenance: {
      source: "engine",
      ruleId: rule.ruleId,
      runId,
      extraction: rule.extraction,
      confidence: rule.confidence,
      observed: rr.observedValue,
      threshold: rr.thresholdApplied,
      status: rr.status,
      exceptionsConsidered: rr.exceptionsConsidered,
      cite: rr.cite,
    },
  };
}

function buildRow(
  measure: WashroomMeasureResult,
  rule: RuleSpec | undefined,
  preferredSheet: string,
  runId: string
): GeometryCheckRow {
  let element: EvaluatedElement;
  let ruleResult: RuleResult | null = null;
  let violation: Violation | null = null;
  let pass: boolean | null = null;

  if (rule) {
    element = toEvaluatedElement(measure);
    ruleResult = evaluateRule(rule, element);
    pass = evaluatePass(measure);
    if (ruleResult && ruleResult.status === "confirmed_violation") {
      violation = ruleResultToGeometryViolation(
        ruleResult,
        rule,
        preferredSheet,
        runId
      );
    }
  } else {
    element = {
      dbId: measure.candidate.dbId,
      ontologyType: "vera:AccessibleWaterCloset",
      displayName: measure.candidate.displayName,
      facts: {},
      factSources: {},
    };
  }

  return { measure, element, ruleResult, violation, pass, source: "washroom" };
}

export const useGeometryStore = create<GeometryStore>((set, get) => ({
  status: "idle",
  error: null,
  rows: [],
  lastRunKey: null,

  clear: () =>
    set({
      status: "idle",
      error: null,
      rows: [],
      lastRunKey: null,
    }),

  runGeometryChecks: async viewer => {
    const engine = useEngineStore.getState();
    if (!engine.enabledJurisdictions["sg-bca"]) {
      set({ status: "idle", rows: [], error: null, lastRunKey: null });
      return;
    }

    const projectId = useProjectStore.getState().currentProjectId;
    const project = getProject(projectId);
    const runKey = `${projectId}:${engine.liveRunId}:bca:${useViewerStore.getState().currentSheetId}`;
    if (get().status === "running") return;
    if (get().lastRunKey === runKey && get().status === "done") return;

    set({ status: "running", error: null, rows: [], lastRunKey: runKey });

    try {
      const bcaRule = findBca513Rule(engine.rules);
      if (!bcaRule) {
        set({
          status: "done",
          rows: [],
          error: null,
          lastRunKey: runKey,
        });
        return;
      }

      const { candidates } = await findWaterClosetsForGeometry(viewer);
      const runId = `geometry-${new Date().toISOString().replace(/[:.]/g, "-")}`;
      const preferredSheet =
        project.defaultSheetByOntologyType["vera:AccessibleWaterCloset"] ??
        project.defaultSheetByOntologyType["vera:Opening"];
      const rows: GeometryCheckRow[] = [];
      const measureOpts = { skipCameraStaging: true };

      await stageModelForRaycast(viewer, 600);

      for (const candidate of candidates) {
        const m = await measureClearAtDbId(
          viewer,
          candidate.dbId,
          candidate.displayName,
          measureOpts
        );
        rows.push(buildRow(m, bcaRule, preferredSheet, runId));
      }

      set({
        status: "done",
        rows,
        error: null,
        lastRunKey: runKey,
      });
    } catch (err) {
      set({
        status: "error",
        error: err instanceof Error ? err.message : String(err),
        rows: [],
        lastRunKey: null,
      });
    }
  },
}));

/** Drive overlay from violations-panel selection. */
export async function visualizeForViolation(
  viewer: Autodesk.Viewing.GuiViewer3D,
  violation: Violation
): Promise<boolean> {
  return visualizeViolation(viewer, violation);
}

/** Confirmed BCA geometry violations only — merged into violations panel. */
export function geometryViolations(rows: GeometryCheckRow[]): Violation[] {
  return rows
    .map(r => r.violation)
    .filter(
      (v): v is Violation =>
        v != null && v.provenance?.status === "confirmed_violation"
    );
}

/** Lookup stored ray-march result for overlay rendering. */
export function geometryRowForDbId(dbId: number): GeometryCheckRow | undefined {
  return useGeometryStore
    .getState()
    .rows.find(r => r.measure.candidate.dbId === dbId);
}
